/**
 * 059: the identity-document sides the platform accepts, grouped for a phone screen. (312: eight —
 * the student card back is no longer collected.)
 *
 * The platform stores nine independent slots and has no notion of a document
 * (`backend/src/modules/users/identity.ts`). Grouping is presentation only — nothing here is
 * sent, and the platform's names are never printed at a customer.
 */

export type IdentitySide =
  | 'ic_front'
  | 'ic_back'
  | 'driving_licence_front'
  | 'driving_licence_back'
  | 'passport_front'
  | 'passport_back'
  | 'selfie'
  | 'student_card_front'
  | 'student_card_back';

export interface DocumentSideEntry {
  side: IdentitySide;
  /** "Front" / "Back", or empty for a document with only one side. */
  label: string;
}

export interface DocumentGroup {
  key: 'ic' | 'driving_licence' | 'passport' | 'student_card' | 'selfie';
  label: string;
  sides: DocumentSideEntry[];
}

export const IDENTITY_GROUPS: DocumentGroup[] = [
  {
    key: 'ic',
    label: 'NRIC',
    sides: [
      { side: 'ic_front', label: 'Front' },
      { side: 'ic_back', label: 'Back' },
    ],
  },
  {
    key: 'driving_licence',
    label: 'Driving licence',
    sides: [
      { side: 'driving_licence_front', label: 'Front' },
      { side: 'driving_licence_back', label: 'Back' },
    ],
  },
  {
    key: 'passport',
    label: 'Passport',
    sides: [
      { side: 'passport_front', label: 'Front' },
      { side: 'passport_back', label: 'Back' },
    ],
  },
  {
    key: 'student_card',
    label: 'Student card',
    // 312 (#538): FRONT ONLY. Many student cards double as ATM/debit cards, so the back can carry
    // bank card details; the platform refuses uploads of it. Never add the back here again.
    sides: [{ side: 'student_card_front', label: 'Front' }],
  },
  {
    key: 'selfie',
    label: 'Selfie',
    sides: [{ side: 'selfie', label: '' }],
  },
];

/**
 * 186 (#29, #31): how compulsory one document side is, as the PLATFORM reports it.
 *
 * 🔴 These three words are the platform's, not this screen's, and the screen must never compute
 * them. `app/` cannot import from `backend/`, so a rule restated here would be a second definition
 * — free to drift from the one the server enforces, with the customer reading whichever one nobody
 * tested. The server publishes this per side on `GET /users/me/identity`; the screen only renders it.
 *
 * `one_of` is its own word rather than being folded into `required` because neither alternative
 * fits: an NRIC is not individually required (a passport will do), but SOME identity document is
 * mandatory, so it is not optional either.
 */
export type SideRequirement = 'required' | 'one_of' | 'optional';

/**
 * The marker a group's heading carries, or `null` for no marker at all.
 *
 * Absent requirements produce `null` everywhere — an older or cached response must not cause this
 * screen to invent a rule.
 */
export function requirementLabel(
  group: DocumentGroup,
  requirements: Partial<Record<IdentitySide, SideRequirement>> | undefined,
  allGroups: DocumentGroup[] = IDENTITY_GROUPS,
): string | null {
  if (!requirements) return null;

  const own = group.sides.map((s) => requirements[s.side]);
  if (own.some((r) => r === undefined)) return null;

  if (own.every((r) => r === 'required')) return 'Required';

  if (own.every((r) => r === 'one_of')) {
    // Name the alternatives, because "Required" alone would be wrong in both directions: a customer
    // holding a passport would read their missing NRIC as a blocker, and a customer with no NRIC
    // would read the screen as saying they cannot proceed.
    // 🔴 Every alternative, in a STABLE order, including this group's own label — so both the NRIC
    // and the Passport card read the identical "Required — NRIC or Passport". Listing the current
    // group first produced "Required — Passport or NRIC" on the passport card, which describes the
    // card rather than the requirement and reads as though the two markers meant different things.
    const alternatives = allGroups
      .filter((g) => g.sides.every((s) => requirements[s.side] === 'one_of'))
      .map((g) => g.label);

    return alternatives.length > 1 ? `Required — ${alternatives.join(' or ')}` : 'Required';
  }

  // Optional, or a mix this screen has no honest sentence for. Mark nothing rather than guess.
  return null;
}
